import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import { useAvailabilitySlots } from '@/hooks/useAvailabilitySlots';
import { useAgendaConfig } from '@/hooks/useAgendaConfig';
import { useAuth } from '@/contexts/AuthContext';
import { format, addDays } from 'date-fns';

// Ícone de Plus (baseado nos assets do Figma)
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 1V15M1 8H15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface InstructorSlotsSectionProps {
  classes?: any[];
  selectedDate?: Date;
}

export const InstructorSlotsSection: React.FC<InstructorSlotsSectionProps> = ({ 
  classes = [], 
  selectedDate = new Date() 
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const { config: agendaConfig } = useAgendaConfig();

  // Buscar configuração da agenda
  const effectiveConfig = (agendaConfig && typeof agendaConfig === 'object' && !Array.isArray(agendaConfig)) ? agendaConfig : {
    instructor_id: user?.id || '',
    working_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    working_hours: {
      mon: { start: '08:00', end: '18:00' },
      tue: { start: '08:00', end: '18:00' },
      wed: { start: '08:00', end: '18:00' },
      thu: { start: '08:00', end: '18:00' },
      fri: { start: '08:00', end: '18:00' }
    },
    default_duration: 60,
    is_public: false
  };

  // Primeiro, tentar a data selecionada
  const currentSlots = useAvailabilitySlots(classes, [], selectedDate, effectiveConfig);
  
  // Calcular próximo dia útil
  const nextWorkingDay = useMemo(() => {
    const workingDays = effectiveConfig.working_days || ['mon', 'tue', 'wed', 'thu', 'fri'];
    let nextDay = selectedDate;
    let attempts = 0;
    const maxAttempts = 7;
    
    while (attempts < maxAttempts) {
      nextDay = addDays(nextDay, 1);
      const dayName = nextDay.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      
      if (workingDays.includes(dayName)) {
        return nextDay;
      }
      
      attempts++;
    }
    
    return selectedDate;
  }, [selectedDate, effectiveConfig]);
  
  // Verificar slots no próximo dia útil
  const nextDaySlots = useAvailabilitySlots(classes, [], nextWorkingDay, effectiveConfig);
  
  // Lógica inteligente para seleção de data e slots
  const { displayDate, displaySlots, isNextDay } = useMemo(() => {
    const now = new Date();
    const isToday = now.toDateString() === selectedDate.toDateString();
    const currentHour = now.getHours();
    
    // Se temos slots hoje, usar hoje
    if (currentSlots && currentSlots.length > 0) {
      return {
        displayDate: selectedDate,
        displaySlots: currentSlots,
        isNextDay: false
      };
    }
    
    // Se é hoje e ainda é cedo (antes das 20h), mostrar hoje mesmo sem slots
    // Isso evita pular para o próximo dia prematuramente
    if (isToday && currentHour < 20) {
      return {
        displayDate: selectedDate,
        displaySlots: currentSlots || [],
        isNextDay: false
      };
    }
    
    // Se não temos slots hoje E é tarde OU não é hoje, usar próximo dia útil
    if (nextDaySlots && nextDaySlots.length > 0) {
      return {
        displayDate: nextWorkingDay,
        displaySlots: nextDaySlots,
        isNextDay: true
      };
    }
    
    // Se não encontramos slots em nenhum dia, usar a data original
    return {
      displayDate: selectedDate,
      displaySlots: currentSlots || [],
      isNextDay: false
    };
  }, [selectedDate, nextWorkingDay, currentSlots, nextDaySlots]);

  // Calcular informações dos slots
  const slotInfo = useMemo(() => {
    
    if (!displaySlots || displaySlots.length === 0) {
      return {
        availableSlots: 0,
        startTime: null,
        endTime: null
      };
    }

    // Calcular total de slots disponíveis
    const totalSlots = displaySlots.reduce((total: number, slot: any) => total + slot.slotCount, 0);
    
    // Encontrar o primeiro e último horário dos slots
    const firstSlot = displaySlots[0];
    const lastSlot = displaySlots[displaySlots.length - 1];
    
    // Converter horários para formato legível
    const formatTime = (timeString: string) => {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    return {
      availableSlots: totalSlots,
      startTime: formatTime(firstSlot.startTime),
      endTime: formatTime(lastSlot.endTime)
    };
  }, [displaySlots]);

  const { availableSlots, startTime, endTime } = slotInfo;

  const handleAddClass = () => {
    // Navega para o calendar e abre o modal de Add Class
    navigate('/calendar', { state: { openAddClassModal: true } });
  };

  return (
    <div className="w-[219px] h-[124px] relative shrink-0">
      {/* Card com borda tracejada grossa como na página "Minhas aulas" */}
      <div 
        className="absolute inset-0 bg-transparent border-2 border-white border-dashed rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" 
      />
      
      {/* Conteúdo */}
      <div className="absolute inset-0 flex flex-col justify-between p-[17px]">
        {/* Título e horário */}
        <div>
          <h3 className="font-['Roboto'] font-bold text-[13px] text-white leading-normal tracking-[0.1px]">
            {availableSlots > 0 ? `${availableSlots} slots available` : 'Slots available'}
            <span className="block text-[11px] font-normal text-gray-300 mt-1">
              {format(displayDate, 'MMM dd, yyyy')}
              {isNextDay && <span className="text-blue-300 ml-1">(next day)</span>}
            </span>
          </h3>
          
          <p className="font-['Roboto'] font-normal text-[12px] text-white leading-[17.5px]">
            {availableSlots > 0 ? (
              <>
                {startTime} - {endTime}
              </>
            ) : (
              isNextDay ? 
                `Next available: ${format(displayDate, 'MMM dd')}` : 
                'No slots available today'
            )}
          </p>
        </div>
        
        {/* Botão Add Class */}
        <div className="flex justify-center">
          <button 
            onClick={handleAddClass}
            className="flex items-center justify-center gap-2 h-6 px-[30px] py-2 border border-white rounded-full bg-transparent hover:bg-white/10 transition-colors"
          >
            <PlusIcon />
            <span className="font-['Roboto'] font-bold text-[12px] text-white leading-normal">
              Add Class
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
